import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowUp, ArrowDown, Equals, Clock, ArrowsClockwise, ChartLineUp,
  UserCircle, UsersThree, Briefcase, TrendDown, Timer, WarningCircle, ChartBar
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

const CATEGORY_CONFIG = {
  unemployment_rate: { icon: TrendDown, color: "#ef4444", label: "Unemployment Rate", accent: "red" },
  labor_participation: { icon: UsersThree, color: "#3b82f6", label: "Labor Force Participation", accent: "blue" },
  labor_force: { icon: UserCircle, color: "#8b5cf6", label: "Civilian Labor Force", accent: "violet" },
  nonfarm_payrolls: { icon: Briefcase, color: "#10b981", label: "Nonfarm Payrolls", accent: "emerald" },
  employment_pop_ratio: { icon: ChartBar, color: "#06b6d4", label: "Employment-Pop Ratio", accent: "cyan" },
  long_term_unemployed: { icon: Timer, color: "#f97316", label: "Long-Term Unemployed (27+ wks)", accent: "orange" },
  u6_rate: { icon: WarningCircle, color: "#f59e0b", label: "U-6 Underemployment Rate", accent: "amber" },
  avg_weeks_unemployed: { icon: Timer, color: "#ec4899", label: "Avg Duration Unemployed", accent: "pink" },
};

function fmtVal(val, key) {
  if (val === null || val === undefined) return "--";
  if (key === "labor_force" || key === "nonfarm_payrolls" || key === "long_term_unemployed") {
    return `${(val / 1000).toFixed(1)}M`;
  }
  if (key === "avg_weeks_unemployed") return `${val.toFixed(1)} wks`;
  return `${val.toFixed(1)}%`;
}

function ChangeIndicator({ value, suffix = "%", invert = false }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  const color = isZero ? "text-zinc-400" : invert ? (isPositive ? "text-red-400" : "text-emerald-400") : (isPositive ? "text-emerald-400" : "text-red-400");
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${color}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value.toFixed(2)}{suffix}
    </span>
  );
}

function HeroCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;
  const invert = ["unemployment_rate", "u6_rate", "long_term_unemployed", "avg_weeks_unemployed"].includes(category);

  return (
    <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-5 space-y-4" data-testid={`unemp-hero-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${cfg.color}15` }}>
            <Icon size={16} weight="bold" style={{ color: cfg.color }} />
          </div>
          <div>
            <h3 className="text-sm font-heading font-bold text-zinc-100">{cfg.label}</h3>
            <p className="text-[10px] text-zinc-500">{data.latest_month} {data.latest_year}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-data font-bold text-zinc-50">{fmtVal(data.latest_value, category)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-3">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Month-over-Month</p>
          <ChangeIndicator value={data.mom_change} invert={invert} />
        </div>
        <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-3">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Year-over-Year</p>
          <ChangeIndicator value={data.yoy_change} invert={invert} />
        </div>
      </div>

      <div>
        <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-2">24-Month Trend</p>
        <CpiChart data={data.history} color={cfg.color} height={130} />
      </div>
    </div>
  );
}

function ComponentCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;
  const invert = ["unemployment_rate", "u6_rate", "long_term_unemployed", "avg_weeks_unemployed"].includes(category);

  return (
    <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4 space-y-3 hover:border-zinc-700 transition-colors" data-testid={`unemp-component-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} weight="bold" style={{ color: cfg.color }} />
          <span className="text-xs font-medium text-zinc-300">{cfg.label}</span>
        </div>
        <span className="text-sm font-data font-bold text-zinc-100">{fmtVal(data.latest_value, category)}</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] text-zinc-600 mb-0.5">MoM</p>
          <ChangeIndicator value={data.mom_change} invert={invert} />
        </div>
        <div className="text-right">
          <p className="text-[9px] text-zinc-600 mb-0.5">YoY</p>
          <ChangeIndicator value={data.yoy_change} invert={invert} />
        </div>
      </div>
      <CpiChart data={data.history} color={cfg.color} height={80} />
      <p className="text-[9px] text-zinc-600">{data.latest_month} {data.latest_year}</p>
    </div>
  );
}

export default function UnemploymentReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("overview");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/unemployment`);
      setReport(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load unemployment data");
    } finally {
      setLoading(false);
    }
  };

  const cats = report?.categories || {};
  const heroKeys = ["unemployment_rate", "labor_participation", "nonfarm_payrolls"];
  const componentKeys = ["labor_force", "employment_pop_ratio", "long_term_unemployed", "u6_rate", "avg_weeks_unemployed"];
  const allAvailable = Object.entries(cats).filter(([k]) => CATEGORY_CONFIG[k]);

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="unemployment-report">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Unemployment Rate</h1>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
            <UsersThree size={12} />
            U.S. labor market data from the Bureau of Labor Statistics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-7 bg-zinc-800/50 p-0.5">
              <TabsTrigger value="overview" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="unemp-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="components" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="unemp-tab-components">All Indicators</TabsTrigger>
            </TabsList>
          </Tabs>
          <button onClick={fetchData} disabled={loading} className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm" data-testid="unemp-refresh-btn">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-center" data-testid="unemp-error">
          <p className="text-xs text-red-400">{error}</p>
          <button onClick={fetchData} className="text-[10px] text-red-300 underline mt-1">Try Again</button>
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-[320px] bg-zinc-900/50" />)}</div>
        </div>
      )}

      {!loading && !error && view === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="unemp-hero-grid">
            {heroKeys.map(key => cats[key] && <HeroCard key={key} category={key} data={cats[key]} />)}
          </div>

          {cats.unemployment_rate && (
            <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm p-4" data-testid="unemp-summary">
              <div className="flex items-center flex-wrap gap-6">
                <div>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Unemployment Rate</p>
                  <p className={`text-lg font-data font-bold ${(cats.unemployment_rate.latest_value || 0) > 5 ? "text-red-400" : (cats.unemployment_rate.latest_value || 0) > 4 ? "text-amber-400" : "text-emerald-400"}`}>
                    {cats.unemployment_rate.latest_value?.toFixed(1)}%
                  </p>
                </div>
                {cats.labor_participation && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Labor Participation</p>
                    <p className="text-lg font-data font-bold text-blue-400">{cats.labor_participation.latest_value?.toFixed(1)}%</p>
                  </div>
                )}
                {cats.nonfarm_payrolls && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Nonfarm Payrolls</p>
                    <p className="text-lg font-data font-bold text-emerald-400">{(cats.nonfarm_payrolls.latest_value / 1000).toFixed(1)}M</p>
                  </div>
                )}
                {cats.u6_rate && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">U-6 Rate</p>
                    <p className="text-lg font-data font-bold text-amber-400">{cats.u6_rate.latest_value?.toFixed(1)}%</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3 font-medium">Labor Market Indicators</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="unemp-components-grid">
              {componentKeys.map(key => cats[key] && <ComponentCard key={key} category={key} data={cats[key]} />)}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && view === "components" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="unemp-all-components">
          {allAvailable.map(([key, data]) => <HeroCard key={key} category={key} data={data} />)}
        </div>
      )}

      {report && (
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/50" data-testid="unemp-footer">
          <p className="text-[10px] text-zinc-600">Source: {report.source}</p>
          <div className="flex items-center gap-1 text-[10px] text-zinc-600">
            <Clock size={10} />
            <span>Updated: {new Date(report.report_date).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

